import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Per-source accuracy flag raised from the Knowledge Agent answer's
 * citation list. A user clicks the Flag action on a CitationRow inside
 * the per-turn sources panel and picks a reason; the result is persisted
 * to the top-level `source_flags/{flagId}` collection.
 *
 * Idempotency: one flag per (userId, messageId, chunkId). The doc id is
 * a deterministic sha256 hash of that tuple so a second submission
 * overwrites the first cleanly. Subsequent submissions update reason +
 * comment in place rather than appending history (operators flagging
 * the same source twice are correcting their pick, not building a
 * timeline).
 *
 * RBAC: any authenticated user with Knowledge Agent access may flag.
 * The Beheer/admin surface to review flags is out of scope here.
 */
export const SourceFlagReason = z.enum(['inaccurate', 'outdated', 'wrong_document', 'other']);
export const SourceFlagEntry = z.object({
    id: DocId,
    tenantId: TenantId,
    userId: DocId,
    chatId: z
        .string()
        .min(1)
        .max(120)
        .regex(/^ch_[a-zA-Z0-9]+$/),
    messageId: z
        .string()
        .min(1)
        .max(120)
        .regex(/^msg_[a-zA-Z0-9]+$/),
    chunkId: z.string().min(1).max(500),
    reason: SourceFlagReason,
    /** Optional free-text rationale. 2000-char hard cap mirrors feedback. */
    comment: z.string().max(2000).optional(),
    createdAt: IsoTimestamp,
});
/**
 * POST /api/v1/agent/source-flag request body. The chunkId is the raw
 * identifier returned on the assistant message's citation list; we keep
 * it as a permissive string (max 500) because the corpus mixes UUIDs,
 * confluence-{pageId}-{n} ids, and code/git derived ids.
 */
export const SourceFlagRequest = z.object({
    chatId: z
        .string()
        .min(1)
        .max(120)
        .regex(/^ch_[a-zA-Z0-9]+$/),
    messageId: z
        .string()
        .min(1)
        .max(120)
        .regex(/^msg_[a-zA-Z0-9]+$/),
    chunkId: z.string().min(1).max(500),
    reason: SourceFlagReason,
    comment: z.string().max(2000).optional(),
});
/**
 * Hydrated per-message flag shape served on the chat-thread response.
 * Keyed by chunkId in the message payload so the frontend can fold it
 * into per-CitationRow state in O(1) without re-querying.
 */
export const HydratedSourceFlag = z.object({
    reason: SourceFlagReason,
    comment: z.string().max(2000).optional(),
});
//# sourceMappingURL=source-flag.js.map